<script setup lang="ts">
import { computed } from "vue";
import { UiCard, UiText, UiAvatar, UiTooltip } from "@shopexperts/shared";
import type { AdminExpertWalletBalance } from "@shopexperts/legacy-api";
import { formatCurrency } from "@/v2Projects/utils/display";
import { getExpertTypeAppearance } from "@/app/components/expert-manager/expertTypeAppearance";
import { LIABILITY_COLORS } from "./walletStatisticsDisplay";

const props = defineProps<{
  topExperts?: AdminExpertWalletBalance[];
  totalOwedCents?: number;
  currency?: string;
  isLoading: boolean;
}>();

const emit = defineEmits<{
  viewTransactions: [organizationId: number];
}>();

const rows = computed(() => {
  const experts = props.topExperts ?? [];
  const maxOwed = experts[0]?.total_owed_cents ?? 0;
  return experts.map((balance, index) => {
    const owed = Math.max(balance.total_owed_cents, 0);
    const widthPct = maxOwed > 0 ? (owed / maxOwed) * 100 : 0;
    return {
      ...balance,
      rank: index + 1,
      widthPct,
      availablePct: owed > 0 ? (Math.max(balance.available_balance_cents, 0) / owed) * 100 : 0,
      displayName: balance.expert?.name ?? balance.organization_name ?? `Organization #${balance.organization_id}`,
      avatarRingClass: getExpertTypeAppearance(
        balance.organization_type === "agency" ? "agency" : "freelancer",
      ).avatarRingClass,
    };
  });
});

const concentrationPct = computed(() => {
  const total = props.totalOwedCents ?? 0;
  if (total <= 0 || rows.value.length === 0) return null;
  const topSum = rows.value.reduce((sum, row) => sum + Math.max(row.total_owed_cents, 0), 0);
  return Math.round((topSum / total) * 100);
});
</script>

<template>
  <UiCard class="h-full">
    <div class="flex h-full flex-col gap-3 p-2">
      <UiText as="h3" variant="h6" class="font-semibold" text="Top owed" />

      <div v-if="isLoading" class="flex flex-1 flex-col gap-3">
        <div v-for="n in 5" :key="n" class="h-9 animate-pulse rounded-md bg-se-gray-100" />
      </div>

      <UiText
        v-else-if="rows.length === 0"
        as="p"
        variant="p-small"
        class="text-gray-500"
        text="No experts are currently owed money."
      />

      <template v-else>
        <ul class="flex flex-col">
          <li v-for="row in rows" :key="row.wallet_id">
            <UiTooltip :content="`View ${row.displayName}'s wallet transactions`">
              <button
                type="button"
                class="grid w-full cursor-pointer grid-cols-[16px_28px_minmax(0,1fr)_auto] items-center gap-2.5 rounded-lg px-1.5 py-2 text-left transition-colors hover:bg-gray-50"
                @click="emit('viewTransactions', row.organization_id)"
              >
                <span class="text-[11px] font-semibold tabular-nums text-gray-400">{{ row.rank }}</span>
                <UiAvatar
                  :name="row.displayName"
                  :url="row.expert?.avatar ?? undefined"
                  size="xxs"
                  :responsive="false"
                  color-coded
                  class="ring-2 ring-offset-1 ring-offset-white"
                  :class="row.avatarRingClass"
                />
                <span class="flex min-w-0 flex-col gap-1">
                  <span class="truncate text-sm font-medium text-se-dark">{{ row.displayName }}</span>
                  <!-- Same emerald/amber split as the overview bar, scaled to the #1 expert -->
                  <span class="flex h-1 overflow-hidden rounded-full bg-gray-100" :style="{ width: `${Math.max(row.widthPct, 4)}%` }">
                    <span class="h-full" :class="LIABILITY_COLORS.available.bar" :style="{ width: `${row.availablePct}%` }" />
                    <span class="h-full flex-1" :class="LIABILITY_COLORS.hold.bar" />
                  </span>
                </span>
                <span class="text-sm font-semibold tabular-nums text-se-dark">
                  {{ formatCurrency(row.total_owed_cents, row.currency) }}
                </span>
              </button>
            </UiTooltip>
          </li>
        </ul>

        <UiText
          v-if="concentrationPct !== null"
          as="p"
          variant="caption-normal"
          class="mt-auto border-t border-se-gray-200 pt-3 text-[11px] uppercase tracking-[0.03em] text-gray-400"
        >
          Top {{ rows.length }} hold {{ concentrationPct }}% of everything owed
        </UiText>
      </template>
    </div>
  </UiCard>
</template>
